import { ApolloClient, InMemoryCache, ApolloLink } from "@apollo/client";

import { HttpLink } from "@apollo/client/link/http";
import { SetContextLink } from "@apollo/client/link/context";
import { ErrorLink } from "@apollo/client/link/error";
import { RemoveTypenameFromVariablesLink } from "@apollo/client/link/remove-typename";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { from, switchMap, catchError, throwError } from "rxjs";
import { getOverrideTenantId } from "../utils/tenantOverride";
import { isAuthenticationError } from "./authError";

/** Minimal shape the client needs from a token manager; both apps' tokenManagers satisfy it. */
export interface TokenManagerLike {
  getAccessToken(): Promise<string | null>;
  hasRefreshToken(): boolean;
  refreshToken(): Promise<string>;
}

export interface CreateApolloClientConfig {
  /** Token manager used for the Authorization header and 401 retry-with-refresh. */
  tokenManager: TokenManagerLike;
  /** Header name carrying the override-tenant id. Apps intentionally use different literal names. */
  overrideHeaderName: string;
  /** Fetch credentials mode for the default HttpLink. */
  credentials: RequestCredentials;
  /** URI used when `resolveUri` returns nothing. */
  defaultUri: string;
  /** Per-operation URI routing (e.g. schema-based endpoint switching). Falls back to `defaultUri`. */
  resolveUri?: (
    operation: ApolloLink.Operation,
    defaultUri: string,
  ) => string | undefined;
  /** Extra headers merged in on every request (e.g. referral tracking). */
  extraHeaders?: () => Record<string, string> | undefined;
  /** Called once a request fails auth and there's no refresh token left to retry with. */
  onSessionExpired: () => void;
  /** Called for every GraphQL/network error, after the auth-retry path has been ruled out. */
  onError?: (error: unknown, operation: ApolloLink.Operation) => void;
  /** Strips `__typename` from variables before sending. Defaults to true. */
  removeTypename?: boolean;
}

/** Config-driven Apollo Client factory shared by backoffice and reseller. */
export function createApolloClient(
  config: CreateApolloClientConfig,
): ApolloClient {
  const {
    tokenManager,
    overrideHeaderName,
    credentials,
    defaultUri,
    resolveUri,
    extraHeaders,
    onSessionExpired,
    onError,
    removeTypename = true,
  } = config;

  const endpointLink = new ApolloLink((operation, forward) => {
    const uri = resolveUri?.(operation, defaultUri) ?? defaultUri;
    operation.setContext({ uri });
    return forward(operation);
  });

  const httpLink = new HttpLink({
    uri: (operation) => {
      const ctxUri = operation.getContext().uri;
      return typeof ctxUri === "string" ? ctxUri : defaultUri;
    },
    credentials,
  });

  const authLink = new SetContextLink(async (prevContext) => {
    const token = await tokenManager.getAccessToken();
    const overrideTenantId = getOverrideTenantId();

    const headers: Record<string, string> = {
      ...prevContext.headers,
      ...(token && { authorization: `Bearer ${token}` }),
      ...extraHeaders?.(),
    };

    if (overrideTenantId) {
      headers[overrideHeaderName] = overrideTenantId;
    }

    return { headers };
  });

  function retryOperationWithFreshToken(
    operation: ApolloLink.Operation,
    forward: ApolloLink.ForwardFunction,
  ) {
    if (!tokenManager.hasRefreshToken()) {
      onSessionExpired();
      return;
    }

    return from(tokenManager.refreshToken()).pipe(
      switchMap((accessToken) => {
        const oldHeaders = operation.getContext().headers;
        operation.setContext({
          headers: {
            ...oldHeaders,
            authorization: `Bearer ${accessToken}`,
          },
        });
        return forward(operation);
      }),
      catchError((refreshError) => {
        console.error("Token refresh after auth error failed:", refreshError);
        onSessionExpired();
        return throwError(() => refreshError);
      }),
    );
  }

  const errorLink = new ErrorLink(({ error, operation, forward }) => {
    if (isAuthenticationError(error)) {
      return retryOperationWithFreshToken(operation, forward);
    }

    if (CombinedGraphQLErrors.is(error)) {
      const operationType =
        operation.query.definitions[0]?.kind === "OperationDefinition"
          ? operation.query.definitions[0].operation
          : "unknown";
      console.error(
        `[GraphQL ${operationType} error]:`,
        operation.operationName || "unnamed",
      );
      error.errors.forEach(({ message, extensions, path }) => {
        console.error("[GraphQL error]:", {
          message,
          path,
          extensions,
          operation: operation.operationName,
        });
      });
    } else {
      console.error("[Network error]:", error);
    }

    onError?.(error, operation);
  });

  const links: ApolloLink[] = [errorLink, authLink];
  if (removeTypename) {
    links.push(new RemoveTypenameFromVariablesLink());
  }
  links.push(endpointLink, httpLink);

  return new ApolloClient({
    link: ApolloLink.from(links),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: { fetchPolicy: "network-only", errorPolicy: "all" },
      query: { fetchPolicy: "network-only", errorPolicy: "all" },
      mutate: { errorPolicy: "all" },
    },
  });
}
